import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Plus, Paperclip, Brain, ChevronRight, Folder } from 'lucide-react';
import '../ui/MenuSurface.css';
import { getReasoningLevelLabel } from '../../utils/reasoningLevels';

const InputActions = ({
  modelDetails,
  isThinking,
  isDeepResearch,
  disableInput,
  onFileSelect,
  onThinkingToggle,
  onDeepResearchToggle,
  reasoningLevel,
  onReasoningLevelChange,
  reasoningLevels = [],
  forceMenuOpen = false,
  onMenuActionSelected,
  projects = [],
  activeProjectId = null,
  onProjectSelect
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const [showReasoningSubmenu, setShowReasoningSubmenu] = useState(false);
  const [showProjectSubmenu, setShowProjectSubmenu] = useState(false);
  const [activeOptionIndex, setActiveOptionIndex] = useState(0);
  const menuRef = useRef(null);
  const buttonRef = useRef(null);
  const menuOptionRefs = useRef([]);
  const openedByForceRef = useRef(false);

  const closeMenu = useCallback(() => {
    setShowMenu(false);
    setShowReasoningSubmenu(false);
    setShowProjectSubmenu(false);
    openedByForceRef.current = false;
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target) &&
          buttonRef.current && !buttonRef.current.contains(event.target)) {
        closeMenu();
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [closeMenu, showMenu]);

  useEffect(() => {
    if (forceMenuOpen && !showMenu) {
      document.dispatchEvent(new CustomEvent('arrow-hide-tooltip'));
      setShowMenu(true);
      setShowReasoningSubmenu(false);
      setShowProjectSubmenu(false);
      setActiveOptionIndex(0);
      openedByForceRef.current = true;
      return;
    }
    if (!forceMenuOpen && showMenu && openedByForceRef.current) {
      closeMenu();
    }
  }, [closeMenu, forceMenuOpen, showMenu]);

  const handleFileClick = useCallback(() => {
    closeMenu();
    onFileSelect();
    onMenuActionSelected?.();
  }, [closeMenu, onFileSelect, onMenuActionSelected]);

  const handleThinkingClick = useCallback(() => {
    closeMenu();
    onThinkingToggle();
    onMenuActionSelected?.();
  }, [closeMenu, onMenuActionSelected, onThinkingToggle]);

  const handleDeepResearchClick = useCallback(() => {
    closeMenu();
    onDeepResearchToggle();
    onMenuActionSelected?.();
  }, [closeMenu, onDeepResearchToggle, onMenuActionSelected]);

  const handleReasoningLevelSelect = useCallback((level) => {
    closeMenu();
    setShowReasoningSubmenu(false);
    if (onReasoningLevelChange) {
      onReasoningLevelChange(level.value);
    }
    onMenuActionSelected?.();
  }, [closeMenu, onMenuActionSelected, onReasoningLevelChange]);

  const handleReasoningMenuClick = useCallback(() => {
    setShowReasoningSubmenu((prev) => !prev);
    setShowProjectSubmenu(false);
    onMenuActionSelected?.();
  }, [onMenuActionSelected]);

  const handleProjectMenuClick = useCallback(() => {
    setShowProjectSubmenu((prev) => !prev);
    setShowReasoningSubmenu(false);
    onMenuActionSelected?.();
  }, [onMenuActionSelected]);

  const handleProjectSelect = useCallback((projectId) => {
    closeMenu();
    onProjectSelect?.(projectId || null);
    onMenuActionSelected?.();
  }, [closeMenu, onMenuActionSelected, onProjectSelect]);

  const featureOptions = useMemo(() => {
    const options = [];
    if (modelDetails?.thinking && reasoningLevels.length === 0) {
      options.push({
        id: 'thinking',
        label: 'Thinking mode',
        icon: Brain,
        onSelect: handleThinkingClick,
        active: Boolean(isThinking),
      });
    }
    if (modelDetails?.deep_research) {
      options.push({
        id: 'deep-research',
        label: 'Deep research',
        icon: Brain,
        onSelect: handleDeepResearchClick,
        active: Boolean(isDeepResearch),
      });
    }
    if (reasoningLevels.length > 0) {
      options.push({
        id: 'reasoning',
        label: `Reasoning: ${getReasoningLevelLabel(reasoningLevel)}`,
        icon: Brain,
        onSelect: handleReasoningMenuClick,
        showChevron: true,
      });
    }
    return options;
  }, [
    handleDeepResearchClick,
    handleReasoningMenuClick,
    handleThinkingClick,
    isDeepResearch,
    isThinking,
    modelDetails?.deep_research,
    modelDetails?.thinking,
    reasoningLevel,
    reasoningLevels.length,
  ]);

  const primaryOptions = useMemo(() => {
    const options = [
      {
        id: 'file',
        label: 'Add photos & files',
        icon: Paperclip,
        onSelect: handleFileClick,
      },
    ];

    return options;
  }, [handleFileClick]);

  const projectOption = useMemo(() => {
    if (!onProjectSelect) return null;
    const activeProject = projects.find((project) => project.id === activeProjectId);
    return {
      id: 'workspace',
      label: activeProject ? `Workspace: ${activeProject.name}` : 'Workspaces',
      icon: Folder,
      onSelect: handleProjectMenuClick,
      showChevron: true,
      active: Boolean(activeProjectId),
    };
  }, [activeProjectId, handleProjectMenuClick, onProjectSelect, projects]);

  const menuOptions = useMemo(
    () => [
      ...primaryOptions,
      ...featureOptions,
      ...(projectOption ? [projectOption] : []),
    ],
    [featureOptions, primaryOptions, projectOption]
  );

  useEffect(() => {
    if (!showMenu || menuOptions.length === 0) {
      return;
    }
    if (activeOptionIndex >= menuOptions.length) {
      setActiveOptionIndex(0);
    }
  }, [activeOptionIndex, menuOptions.length, showMenu]);

  useEffect(() => {
    if (!showMenu) {
      return;
    }

    const handleMenuKeyDown = (event) => {
      if (menuOptions.length === 0) {
        return;
      }

      if ((showReasoningSubmenu || showProjectSubmenu) && event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        setShowReasoningSubmenu(false);
        setShowProjectSubmenu(false);
        return;
      }

      if (
        event.key !== 'ArrowDown' &&
        event.key !== 'ArrowUp' &&
        event.key !== 'Enter' &&
        event.key !== 'Escape'
      ) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      if (event.key === 'ArrowDown') {
        setShowReasoningSubmenu(false);
        setShowProjectSubmenu(false);
        setActiveOptionIndex((prev) => (prev + 1) % menuOptions.length);
        return;
      }

      if (event.key === 'ArrowUp') {
        setShowReasoningSubmenu(false);
        setShowProjectSubmenu(false);
        setActiveOptionIndex((prev) =>
          prev === 0 ? menuOptions.length - 1 : prev - 1
        );
        return;
      }

      if (event.key === 'Enter') {
        const selectedOption = menuOptions[activeOptionIndex];
        selectedOption?.onSelect?.();
        return;
      }

      if (event.key === 'Escape') {
        closeMenu();
      }
    };

    document.addEventListener('keydown', handleMenuKeyDown, true);
    return () => {
      document.removeEventListener('keydown', handleMenuKeyDown, true);
    };
  }, [activeOptionIndex, closeMenu, menuOptions, showMenu, showProjectSubmenu, showReasoningSubmenu]);

  const setOptionRef = useCallback((index, node) => {
    menuOptionRefs.current[index] = node;
  }, []);

  const renderOption = (option, index) => {
    const Icon = option.icon;
    const isActiveRow = index === activeOptionIndex;
    return (
      <button
        key={option.id}
        ref={(node) => setOptionRef(index, node)}
        onMouseEnter={() => {
          setActiveOptionIndex(index);
          if (option.id === 'workspace') {
            setShowProjectSubmenu(true);
            setShowReasoningSubmenu(false);
          } else if (option.id === 'reasoning') {
            setShowReasoningSubmenu(true);
            setShowProjectSubmenu(false);
          } else {
            setShowReasoningSubmenu(false);
            setShowProjectSubmenu(false);
          }
        }}
        onClick={option.onSelect}
        className={`arrow-menu-item ${isActiveRow ? 'active' : ''}`}
      >
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <Icon size={18} strokeWidth={2} className="arrow-menu-icon" />
          <span className="arrow-menu-label">{option.label}</span>
        </div>
        {option.showChevron ? (
          <ChevronRight size={16} strokeWidth={2} className="arrow-menu-chevron" />
        ) : option.active ? (
          <div className="arrow-menu-selected-dot" />
        ) : null}
      </button>
    );
  };

  return (
    <div className="relative group">
      <button
        ref={buttonRef}
        onClick={() => {
          setShowMenu((prev) => {
            const next = !prev;
            if (next) {
              document.dispatchEvent(new CustomEvent('arrow-hide-tooltip'));
              setActiveOptionIndex(0);
            } else {
              setShowReasoningSubmenu(false);
              setShowProjectSubmenu(false);
            }
            return next;
          });
          openedByForceRef.current = false;
        }}
        disabled={disableInput}
        aria-expanded={showMenu}
        data-arrow-menu-open={showMenu ? 'true' : undefined}
        data-arrow-tooltip-disabled={showMenu ? 'true' : undefined}
        aria-label="Add files and more"
        title={showMenu ? undefined : 'Add files and more'}
        className="
          h-11 w-11
          rounded-full
          flex
          items-center
          justify-center
          transition-colors
          duration-200
          hover:bg-gray-200 dark:hover:bg-gray-600
          text-gray-600
          dark:text-gray-400
        "
      >
        <Plus size={24} />
      </button>

      {showMenu && (
        <div
          ref={menuRef}
          className="arrow-menu-surface arrow-input-action-menu absolute bottom-full left-0 z-50 mb-2.5"
        >
          {primaryOptions.map((option, index) => renderOption(option, index))}

          {featureOptions.length > 0 && (
            <div className="arrow-menu-divider" />
          )}

          {featureOptions.map((option, index) =>
            renderOption(option, index + primaryOptions.length)
          )}

          {projectOption && (
            <>
              <div className="arrow-menu-divider" />
              {renderOption(projectOption, primaryOptions.length + featureOptions.length)}
            </>
          )}

          {/* Reasoning level selector */}
          {reasoningLevels.length > 0 && (
            <div className="relative">
              {showReasoningSubmenu && (
                <div className="arrow-menu-surface arrow-input-submenu absolute left-full top-0 ml-2">
                  {reasoningLevels.map((level) => (
                    <button
                      key={level.value}
                      onClick={() => handleReasoningLevelSelect(level)}
                      className="arrow-menu-item"
                    >
                      <div className="flex flex-col items-start">
                        <span className="arrow-menu-label">{level.label}</span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">{level.subtitle}</span>
                      </div>
                      {String(reasoningLevel || '').toLowerCase() === level.value && (
                        <div className="arrow-menu-selected-dot" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {projectOption && showProjectSubmenu && (
            <div className="arrow-menu-surface arrow-input-submenu absolute bottom-0 left-full ml-2">
              <button
                type="button"
                onClick={() => handleProjectSelect(null)}
                className="arrow-menu-item"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <Folder size={18} strokeWidth={2} className="arrow-menu-icon" />
                  <span className="arrow-menu-label">No workspace</span>
                </div>
                {!activeProjectId && <div className="arrow-menu-selected-dot" />}
              </button>

              {projects.length > 0 && (
                <div className="arrow-menu-divider" />
              )}

              {projects.map((project) => (
                <button
                  key={project.id}
                  type="button"
                  onClick={() => handleProjectSelect(project.id)}
                  className="arrow-menu-item"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <Folder size={18} strokeWidth={2} className="arrow-menu-icon" />
                    <span className="arrow-menu-label">
                      {project.name || 'Untitled Workspace'}
                    </span>
                  </div>
                  {activeProjectId === project.id && <div className="arrow-menu-selected-dot" />}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default InputActions;
